import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import OTPVerification from './components/OTPVerification';
import TDSReportForm from './pages/TDSReportForm';
import TDSReportPDFWrapper from './pages/TDSReportPdf';
import { apiConfig } from './config/api';

const getFriendlyErrorMessage = (payload, fallbackMessage) => {
  const message = payload?.message || payload?.error || '';

  if (!message) {
    return fallbackMessage;
  }

  const normalizedMessage = message.toLowerCase();

  if (
    normalizedMessage.includes("couldn't verify this pan and mobile number combination") ||
    normalizedMessage.includes("pan and phone number do not match") ||
    normalizedMessage.includes("do not match any records")
  ) {
    return 'We could not match that PAN and mobile number for the selected fiscal year. Please recheck both details and try again.';
  }

  if (normalizedMessage.includes('no tds data found')) {
    return 'No TDS report is available for the PAN, mobile number, and fiscal year you entered.';
  }

  if (normalizedMessage.includes('missing required fields')) {
    return 'Please complete PAN number, mobile number, and fiscal year before continuing.';
  }

  if (normalizedMessage.includes('server error')) {
    return 'Something went wrong on our side. Please try again in a moment.';
  }

  return message;
};

function App() {
  const [tdsData, setTdsData] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch TDS data based on provided panNo, phoneNo, and fiscalYear
  const fetchTdsData = async (panNo, phoneNo, fiscalYear) => {
    try {
      const response = await fetch(`${apiConfig.tdsApi}/fetch-tds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ panNo, phoneNo, fiscalYear }),
      });

      const data = await response.json();

      if (response.ok && data && Object.keys(data).length > 0) {
        const tdsData = {
          panNo: data.pan_number || 'N/A',
          phoneNo: data.phone_number || 'N/A',
          fiscalYear: data.fiscalYear || fiscalYear,
          agentName: data.agent || 'N/A',
          tdsAmount: data.tds_amount || 'N/A',
          taxableAmount: data.taxable_amount || 'N/A',
        };
        setTdsData(tdsData);
      } else {
        setError(getFriendlyErrorMessage(data, 'No TDS report is available for the details you entered.'));
      }
    } catch (err) {
      setError('We were unable to fetch your TDS details right now. Please try again shortly.');
    }
  };

  // Form submission handling
  const handleFormSubmit = async (formData, onSuccess) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiConfig.tdsApi}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(getFriendlyErrorMessage(data, 'We could not verify your details right now. Please try again.'));
        return;
      }

      if (data.matchFound) {
        setTdsData(data);
        onSuccess();
      } else {
        setError(getFriendlyErrorMessage(data, 'We could not verify the PAN and mobile number you entered.'));
      }
    } catch (err) {
      setError('We could not verify your details right now. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // OTP verification handling
  const handleOtpVerification = async (otpData) => {
    const phoneNo = otpData.phoneNo || otpData.phone_number;
    otpData.phoneNo = phoneNo;
    const agentName = otpData.agentName || otpData.agent;
    otpData.agentName = agentName;
    const tdsAmount = otpData.tdsAmount || otpData.tds_amount;
    otpData.tdsAmount = tdsAmount;
    const taxableAmount = otpData.taxableAmount || otpData.taxable_amount;
    otpData.taxableAmount = taxableAmount;
    const fiscalYear = otpData.fiscalYear || otpData.fiscalYear;
    otpData.fiscalYear = fiscalYear;
    const panNo = otpData.panNo || otpData.pan_number;
    otpData.panNo = panNo;

    if (!phoneNo || !otpData.otp || typeof otpData.isVerified !== "boolean") {
      setError('Phone number and OTP are required to complete verification.');
      return;
    }

    try {
      const otpResponse = await fetch(`${apiConfig.otpApi}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(otpData),
      });

      const otpResult = await otpResponse.json();

      if (otpResponse.ok && otpResult.success) {
        const updatedTdsData = {
          ...tdsData,
          phoneNo: otpData.phoneNo,
          isVerified: true,
          otp: otpData.otp,
          agentName: otpData.agentName, // Use fallback if necessary
          tdsAmount: otpData.tdsAmount,
          taxableAmount: otpData.taxableAmount,
          fiscalYear: otpData.fiscalYear,
          panNo: otpData.panNo,
        };

        setIsVerified(true);
        setTdsData(updatedTdsData);
      } else {
        setError('The OTP you entered is not valid. Please try again with the latest code.');
      }
    } catch (error) {
      setError('We could not verify the OTP right now. Please try again.');
    }
  };

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            <TDSReportForm onSubmit={handleFormSubmit} loading={loading} error={error} />
          }
        />
        <Route
          path="/otp-verification"
          element={
            <OTPVerification onVerified={handleOtpVerification} tdsData={tdsData} />
          }
        />
        <Route
          path="/TDSReportPdf"
          element={
            isVerified && tdsData ? (
              <TDSReportPDFWrapper updatedTdsData={tdsData} />
            ) : (
              <Navigate to="/otp-verification" replace />
            )
          }
        />
      </Routes>

      {/* Conditional redirect to TDSReportPdf if verification is complete */}
      {isVerified && (
        <Navigate to="/TDSReportPdf" replace state={{ updatedTdsData: tdsData, isVerified }} />
      )}
    </Router>
  );
}

export default App;
